import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, Trash2, LogOut, ShieldCheck, AlertTriangle, UserX, Coffee, Bell, BellOff, Clock, MapPin, Crown, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Favourites } from "./Favourites";
import { SessionLog } from "@/components/SessionLog";
import { ProGate } from "@/components/ProGate";
import { useSubscription } from "@/hooks/useSubscription";
import { usePushSubscription } from "@/hooks/use-push-subscription";
import { surfbreaks } from "@/data/surfbreaks";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type Photo = {
  id: string;
  storage_path: string;
  caption: string | null;
  created_at: string;
  ai_labels: any;
  url: string;
};

type ValidationResult = {
  isSurf: boolean;
  hasWaves: boolean;
  hasSurfer: boolean;
  hasBoard: boolean;
  reason: string;
};

const ALERT_TIMES = ["5:00 AM", "6:00 AM", "7:00 AM", "8:00 AM", "9:00 AM"];

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const Profile = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const { subscribe, unsubscribe, subscribing, error: pushError } = usePushSubscription();
  const { isPro, isLoading: subLoading } = useSubscription();

  const [profile, setProfile] = useState<{ display_name: string | null; bio: string | null }>({ display_name: "", bio: "" });
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [caption, setCaption] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertTime, setAlertTime] = useState("6:00 AM");
  const [homeBreakId, setHomeBreakId] = useState<string>("");
  const [savingAlert, setSavingAlert] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: prof } = await supabase
        .from("profiles")
        .select("display_name, bio, alert_enabled, alert_time, home_break_id")
        .eq("id", user.id)
        .maybeSingle();
      if (prof) {
        setProfile({ display_name: prof.display_name ?? "", bio: prof.bio ?? "" });
        setAlertEnabled((prof as any).alert_enabled ?? false);
        setAlertTime((prof as any).alert_time ?? "6:00 AM");
        setHomeBreakId((prof as any).home_break_id ?? "");
      }

      const { data: ph } = await supabase
        .from("surf_photos")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (ph) {
        setPhotos(
          ph.map((p) => ({
            ...p,
            url: supabase.storage.from("surf-photos").getPublicUrl(p.storage_path).data.publicUrl,
          }))
        );
      }
    })();
  }, [user]);

  if (authLoading) {
    return (
      <div className="rounded-3xl bg-card/80 border border-border p-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded-3xl bg-card/80 border border-border p-12 text-center space-y-4">
        <h2 className="font-display text-2xl sm:text-3xl">Sign in to build your surf profile</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Upload your best surf shots — every photo is AI-checked for waves, surfers and boards before going live.
        </p>
        <Button asChild className="bg-gradient-sunset text-accent-foreground hover:opacity-90">
          <Link to="/auth">Sign in or create account</Link>
        </Button>
      </div>
    );
  }

  const saveProfile = async () => {
    setSavingProfile(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: profile.display_name, bio: profile.bio })
      .eq("id", user.id);
    setSavingProfile(false);
    if (error) toast.error(error.message);
    else toast.success("Profile saved");
  };

  const handleAlertToggle = async () => {
    const next = !alertEnabled;
    setAlertEnabled(next);

    if (next) {
      // Request push permission when turning on
      const ok = await subscribe(user.id);
      if (!ok) {
        setAlertEnabled(false);
        toast.error(pushError || "Couldn't enable notifications — please allow them in your browser settings");
      }
    } else {
      await unsubscribe(user.id);
    }
  };

  const saveAlert = async () => {
    if (alertEnabled && !homeBreakId) {
      toast.error("Please select your home break first");
      return;
    }

    setSavingAlert(true);

    // Store home_break_id as "lat,lon,Break Name" for the edge function
    const selectedBreak = surfbreaks.find(b => b.id === homeBreakId);
    const homeBreakValue = selectedBreak
      ? `${selectedBreak.lat},${selectedBreak.lon},${selectedBreak.name}`
      : null;

    const { error } = await supabase
      .from("profiles")
      .update({
        alert_enabled: alertEnabled,
        alert_time: alertTime,
        home_break_id: homeBreakValue,
      } as any)
      .eq("id", user.id);

    setSavingAlert(false);

    if (error) {
      toast.error("Couldn't save alert settings");
    } else {
      toast.success(
        alertEnabled
          ? `Alert set ✓ We'll check ${selectedBreak?.name} every evening`
          : "Alerts turned off"
      );
    }
  };

  const deleteAccount = async () => {
    setDeletingAccount(true);
    try {
      if (photos.length > 0) {
        await supabase.storage.from("surf-photos").remove(photos.map((p) => p.storage_path));
      }
      await supabase.from("surf_photos").delete().eq("user_id", user.id);
      await supabase.from("profiles").delete().eq("id", user.id);
      const { error } = await supabase.functions.invoke("delete-user", { body: { userId: user.id } });
      if (error) throw error;
      await signOut();
      toast.success("Account deleted. Sorry to see you go 🤙");
    } catch (err: any) {
      toast.error(err.message || "Failed to delete account. Please contact hello@barrelcast.surf");
      setDeletingAccount(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB per photo"); return; }

    setValidating(true);
    let validation: ValidationResult;
    try {
      const base64 = await fileToBase64(file);
      const { data, error } = await supabase.functions.invoke("validate-surf-photo", {
        body: { imageBase64: base64, mimeType: file.type },
      });
      if (error) throw error;
      validation = data as ValidationResult;
    } catch (err: any) {
      setValidating(false);
      toast.error(err.message || "AI check failed");
      return;
    }
    setValidating(false);

    if (!validation.isSurf) {
      toast.error("Photo rejected", {
        description: validation.reason || "We couldn't detect waves, a surfer or a surfboard.",
        icon: <AlertTriangle className="h-4 w-4" />,
      });
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("surf-photos").upload(path, file, {
        cacheControl: "3600",
        contentType: file.type,
      });
      if (upErr) throw upErr;

      const { data: row, error: insErr } = await supabase
        .from("surf_photos")
        .insert({ user_id: user.id, storage_path: path, caption: caption || null, ai_labels: validation as any, approved: true })
        .select()
        .single();
      if (insErr) throw insErr;

      const url = supabase.storage.from("surf-photos").getPublicUrl(path).data.publicUrl;
      setPhotos((prev) => [{ ...(row as any), url }, ...prev]);
      setCaption("");
      toast.success("Photo approved & uploaded", { description: validation.reason, icon: <ShieldCheck className="h-4 w-4" /> });
    } catch (err: any) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (p: Photo) => {
    if (!confirm("Delete this photo?")) return;
    await supabase.storage.from("surf-photos").remove([p.storage_path]);
    await supabase.from("surf_photos").delete().eq("id", p.id);
    setPhotos((prev) => prev.filter((x) => x.id !== p.id));
  };

  const busy = uploading || validating;

  return (
    <div className="space-y-6">

      {/* ── Profile card ── */}
      <div className="rounded-3xl border border-border bg-card/80 backdrop-blur p-6">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-2xl">Your profile</h2>
            <p className="text-xs text-muted-foreground mt-1 truncate">{user.email}</p>
          </div>
          <Button variant="outline" size="sm" onClick={signOut} className="shrink-0">
            <LogOut className="h-3.5 w-3.5 sm:mr-1.5" />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>

        {/* Current plan */}
        {!subLoading && (
          isPro ? (
            <div className="flex items-center gap-2 mb-5 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3">
              <Crown className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm font-semibold text-foreground flex-1">Barrelcast Pro</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary">Active</span>
            </div>
          ) : (
            <Link
              to="/subscribe"
              className="flex items-center gap-2 mb-5 rounded-2xl border border-border bg-background/60 px-4 py-3 hover:border-primary/40 transition-colors group"
            >
              <span className="text-sm font-semibold text-foreground">Free plan</span>
              <span className="text-xs text-muted-foreground flex-1">Upgrade for alerts, favourites & more</span>
              <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-primary shrink-0">
                Go Pro <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          )
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="dn">Display name</Label>
            <Input id="dn" value={profile.display_name ?? ""} onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea id="bio" rows={2} placeholder="Home break, favourite board, surf vibe…" value={profile.bio ?? ""} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} />
          </div>
        </div>
        <Button onClick={saveProfile} disabled={savingProfile} className="mt-4">
          {savingProfile && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save profile
        </Button>
      </div>

      {/* ── Worth it tomorrow alert ── */}
      <div className="rounded-3xl border border-border bg-card/80 backdrop-blur p-6">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="h-4 w-4 text-primary" />
          <h3 className="font-display text-xl">Worth it tomorrow?</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          We check your home break every evening and send you a single verdict — paddle out or sleep in. No spam, just the call.
        </p>

        {/* Toggle */}
        <div className="flex items-center justify-between py-3 border-t border-border">
          <div>
            <div className="text-sm font-semibold text-foreground">Morning alert</div>
            <div className="text-xs text-muted-foreground">Sent the evening before</div>
          </div>
          <button
            onClick={handleAlertToggle}
            disabled={subscribing}
            className={`relative w-12 h-6 rounded-full transition-colors ${alertEnabled ? "bg-primary" : "bg-muted"} disabled:opacity-50`}
          >
            {subscribing ? (
              <Loader2 className="h-3 w-3 animate-spin absolute inset-0 m-auto text-white" />
            ) : (
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${alertEnabled ? "translate-x-6" : "translate-x-0"}`} />
            )}
          </button>
        </div>

        {/* Home break + time picker — only shown when enabled */}
        {alertEnabled && (
          <div className="mt-4 space-y-4">
            {/* Home break selector */}
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                <MapPin className="h-3 w-3" /> My home break
              </div>
              <select
                value={homeBreakId}
                onChange={(e) => setHomeBreakId(e.target.value)}
                className="w-full bg-background border border-border text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
              >
                <option value="">Select a break…</option>
                {surfbreaks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} — {b.country}
                  </option>
                ))}
              </select>
            </div>

            {/* Time picker */}
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                <Clock className="h-3 w-3" /> I surf at
              </div>
              <div className="flex gap-2 flex-wrap">
                {ALERT_TIMES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setAlertTime(t)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-all ${
                      alertTime === t
                        ? "bg-gradient-sunset text-accent-foreground border-transparent"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground pt-2">
                We'll check conditions at 8pm the night before and notify you if it's worth paddling out for {alertTime}.
              </p>
            </div>
          </div>
        )}

        <Button
          onClick={saveAlert}
          disabled={savingAlert || subscribing}
          className="mt-5 bg-gradient-sunset text-accent-foreground hover:opacity-90"
        >
          {savingAlert ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
          ) : alertEnabled ? (
            <><Bell className="h-4 w-4 mr-2" /> Save alert</>
          ) : (
            <><BellOff className="h-4 w-4 mr-2" /> Alerts off</>
          )}
        </Button>
      </div>

      {/* ── Support Barrelcast ── */}
      <div className="rounded-3xl border border-primary/20 bg-primary/5 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Coffee className="h-4 w-4 text-primary" />
          <h3 className="font-display text-xl">Support Barrelcast</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Barrelcast is built by one person, fuelled by coffee and salt water. If it's helped you catch a better wave, buying a coffee keeps the buoys online.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href="https://ko-fi.com/barrelcastsurf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 bg-gradient-sunset text-accent-foreground font-bold text-sm uppercase tracking-widest px-5 py-3 hover:opacity-90 transition-opacity"
          >
            <Coffee className="h-4 w-4" /> Buy me a coffee
          </a>
          <a
            href="https://ko-fi.com/barrelcastsurf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 border border-primary/30 text-primary font-bold text-sm uppercase tracking-widest px-5 py-3 hover:bg-primary/10 transition-colors"
          >
            ☕ × 1 — Buy a round
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Opens Ko-fi · No account needed · One-time or monthly
        </p>
      </div>

      {/* ── Photos + Favourites tabs ── */}
      <Tabs defaultValue="photos" className="w-full">
        <TabsList className="w-full grid grid-cols-3 rounded-full bg-card/80 border border-border p-1 h-auto">
          <TabsTrigger value="photos" className="rounded-full data-[state=active]:bg-gradient-sunset data-[state=active]:text-accent-foreground text-xs sm:text-sm py-2">
            Photos
          </TabsTrigger>
          <TabsTrigger value="favourites" className="rounded-full data-[state=active]:bg-gradient-sunset data-[state=active]:text-accent-foreground text-xs sm:text-sm py-2">
            Favourites
          </TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-full data-[state=active]:bg-gradient-sunset data-[state=active]:text-accent-foreground text-xs sm:text-sm py-2">
            Sessions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="photos" className="mt-6 space-y-6">
          <div className="rounded-3xl border border-border bg-card/80 backdrop-blur p-6">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-primary" />
              <h3 className="font-display text-xl">Upload a surf photo</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Each photo is reviewed by AI for waves, surfers and surfboards. Non-surf photos are rejected.
            </p>
            <div className="space-y-3">
              <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" disabled={busy} />
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
              <Button onClick={() => fileInputRef.current?.click()} disabled={busy} className="w-full bg-gradient-sunset text-accent-foreground hover:opacity-90">
                {validating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> AI checking photo…</>
                  : uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
                  : <><Camera className="h-4 w-4 mr-2" /> Choose photo</>}
              </Button>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-card/80 backdrop-blur p-6">
            <h3 className="font-display text-xl mb-4">Your gallery ({photos.length})</h3>
            {photos.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No photos yet. Upload your first surf shot!</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((p) => (
                  <div key={p.id} className="group relative rounded-2xl overflow-hidden border border-border aspect-square">
                    <img src={p.url} alt={p.caption || "Surf photo"} className="w-full h-full object-cover" loading="lazy" />
                    {p.caption && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-background/90 to-transparent p-2">
                        <p className="text-xs font-medium line-clamp-2">{p.caption}</p>
                      </div>
                    )}
                    <button
                      onClick={() => deletePhoto(p)}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground"
                      aria-label="Delete photo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="favourites" className="mt-6">
          <Favourites />
        </TabsContent>

        <TabsContent value="sessions" className="mt-6">
          <ProGate feature="session_logging" blur={false}>
            <SessionLog />
          </ProGate>
        </TabsContent>
      </Tabs>

      {/* ── Danger zone ── */}
      <div className="rounded-3xl border border-destructive/30 bg-destructive/5 p-6">
        <h3 className="font-display text-lg text-destructive mb-1">Danger zone</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Permanently delete your account, profile, and all uploaded photos. This cannot be undone.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground" disabled={deletingAccount}>
              {deletingAccount ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Deleting…</> : <><UserX className="h-3.5 w-3.5 mr-2" /> Delete my account</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete your account?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete your profile, all your surf photos, and your login. Your data cannot be recovered after deletion. This action is irreversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Yes, delete everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
